import { ReactElement, useState } from 'react'
import { useForm } from '@tanstack/react-form'

import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Paper from '@mui/material/Paper'
import TextField from '@mui/material/TextField'

import axios, { pluckError } from 'src/utilities/http-client'
import { CancelIcon, SubmitIcon } from 'src/v3/core/icons'
import { useAppDispatch } from 'src/store/hooks'

import { fetchEMSID } from 'src/v3/profile/profile.slice'
import { EMSID } from 'src/v3/core/types'

type EMSIDFormProps = {
  emsID?: EMSID
  open: boolean
  setOpen: (status: boolean) => void
  onError?: (error: string) => void
}

export let EMSIDForm = ({
  emsID,
  open,
  setOpen,
  onError,
}: EMSIDFormProps): ReactElement => {
  let dispatch = useAppDispatch()

  let [saving, setSaving] = useState(false)

  let form = useForm({
    defaultValues: {
      number: emsID?.emsidNumber || '',
    },
    onSubmit: async ({ value, formApi }) => {
      let request = !emsID ? axios.post : axios.put
      setSaving(true)
      request('/api/profile/ems-id', {
        emsidNumber: value.number,
      })
        .then(() => dispatch(fetchEMSID()))
        .then(() => setOpen(false))
        .then(() => formApi.reset())
        .catch((err) => {
          onError?.(pluckError(err, 'Error Updating EMS ID'))
        })
        .finally(() => setSaving(false))
    },
  })

  let handleOnClose = () => {
    form.reset()
    setOpen(false)
  }

  return (
    <Dialog open={open} onClose={handleOnClose}>
      <Paper sx={{ width: 300, px: 2, py: 1 }}>
        <DialogTitle sx={{ textAlign: 'center', mb: -3 }}>
          {!emsID ? 'Add' : 'Edit'} EMS ID
        </DialogTitle>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            e.stopPropagation()
            form.handleSubmit()
          }}
        >
          <DialogContent
            sx={{
              px: 2,
              pb: 1,
              display: 'flex',
              gap: 2,
              flexDirection: 'column',
            }}
          >
            <form.Field
              name="number"
              validators={{
                onChange: ({ value }) =>
                  !value
                    ? 'ID Number is a required field.'
                    : !/^\d{12}$/.test(value)
                      ? 'EMS ID must be 12 digits'
                      : undefined,
              }}
              children={(field) => (
                <TextField
                  sx={{ bgcolor: 'background.paper' }}
                  fullWidth
                  variant="outlined"
                  value={field.state.value}
                  label="EMS ID"
                  name={field.name}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  error={field.state.meta.errors.length > 0}
                  helperText={field.state.meta.errors.join(', ')}
                />
              )}
            />
          </DialogContent>
          <DialogActions
            sx={{
              display: 'flex',
              justifyContent: 'center',
              pb: 1.5,
            }}
          >
            <form.Subscribe
              selector={(state) => state.isValid}
              children={(isValid) => (
                <Button
                  color="primary"
                  type="submit"
                  variant="contained"
                  sx={{
                    flex: '1 1 0px',
                  }}
                  startIcon={<SubmitIcon />}
                  disabled={!isValid}
                  loading={saving}
                >
                  Submit
                </Button>
              )}
            />
            <Button
              color="error"
              onClick={handleOnClose}
              variant="contained"
              sx={{
                flex: '1 1 0px',
              }}
              startIcon={<CancelIcon />}
            >
              Cancel
            </Button>
          </DialogActions>
        </form>
      </Paper>
    </Dialog>
  )
}
